'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import type { SectionOf } from '@/lib/home-v2/schema';
import { Btn, WRAP } from './ui';

const TONES = {
  yellow: { box: 'bg-hv-yellow text-hv-ink', eyebrow: 'text-hv-ink/70', btn: 'dark' as const },
  dark: { box: 'bg-hv-ink text-white', eyebrow: 'text-hv-yellow', btn: 'yellow' as const },
  orange: { box: 'bg-hv-orange text-white', eyebrow: 'text-white/80', btn: 'white' as const },
};

function parseIst(value: string) {
  return new Date(value.length === 10 ? `${value}T23:59:59+05:30` : value);
}

function useCountdown(target?: string) {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!target) return;
    const end = parseIst(target).getTime();
    const tick = () => setLeft(Math.max(0, end - Date.now()));
    tick();
    const t = setInterval(tick, 60_000);
    return () => clearInterval(t);
  }, [target]);
  return left;
}

/** Time-boxed campaign banner (e.g. griha pravesh / festive season), shown only inside its date window. */
export function SeasonalBanner({ banner }: { banner: SectionOf<'seasonal-banner'> }) {
  const tone = TONES[banner.tone];
  const left = useCountdown(banner.countdownTo);
  const parts = left === null ? null : [
    { v: Math.floor(left / 86_400_000), l: banner.units.days },
    { v: Math.floor((left % 86_400_000) / 3_600_000), l: banner.units.hours },
    { v: Math.floor((left % 3_600_000) / 60_000), l: banner.units.minutes },
  ];
  return (
    <section className={`${WRAP} pt-8 md:pt-12`}>
      <div className={`relative grid items-center gap-5 overflow-hidden rounded-[20px] p-5 md:grid-cols-[1.4fr_auto_auto] md:gap-10 md:p-10 ${tone.box} ${banner.image ? 'md:pr-[34%]' : ''}`}>
        {banner.image && (
          <div className="absolute inset-y-0 right-0 hidden w-[32%] md:block">
            <Image src={banner.image} alt="" fill sizes="460px" className="object-cover" />
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-2">
          {banner.eyebrow && <div className={`font-hv-mono text-[11px] uppercase md:text-xs ${tone.eyebrow}`}>{banner.eyebrow}</div>}
          <h2 className="break-words text-2xl leading-tight md:text-[36px]">{banner.title}</h2>
          {banner.text && <p className="break-words opacity-80">{banner.text}</p>}
        </div>
        {parts && (
          <div className="flex min-w-0 flex-wrap items-end gap-x-3 gap-y-1" aria-label={`${banner.countdownLabel} ${parts.map((p) => `${p.v} ${p.l}`).join(' ')}`}>
            <span className="self-center font-hv-mono text-[11px] uppercase opacity-70">{banner.countdownLabel}</span>
            {parts.map((p) => (
              <span key={p.l} className="flex min-w-0 flex-col items-center">
                <span className="text-3xl font-semibold leading-none md:text-[44px]">{String(p.v).padStart(2, '0')}</span>
                <span className="font-hv-mono text-[10px] uppercase opacity-70">{p.l}</span>
              </span>
            ))}
          </div>
        )}
        {banner.cta && <div className="min-w-0"><Btn href={banner.cta.href} kind={tone.btn} arrow>{banner.cta.label}</Btn></div>}
      </div>
    </section>
  );
}
